/**
 * ADMINISTRAÇÃO DO HUB: AS REGRAS DA ESCRITA (11/10/2026, medição do App.tsx). Usuários, permissões, histórico, produto e
 * DDR em lote. Funções puras que o App usa e os testes cobram dos dois lados. Confirms: título curto, nomes no corpo.
 */
import { fmtDate } from './formatters';

// ---------------------------------------------------------------- Usuários (D4, D5, D6, D7)

type Pessoa = { id: number; name?: string; email?: string; role?: string; isActive?: boolean };

export function adminsAtivos(usuarios: Pessoa[]): number {
  return usuarios.filter((u) => u.role === 'admin' && u.isActive !== false).length;
}

/**
 * D4: por que o PAPEL e o ATIVO deste usuário não se mexem na tela. Dois casos: o próprio usuário (trocar o próprio
 * papel para viewer ou se desativar derruba o acesso de quem está mexendo) e o ÚLTIMO admin ativo (sem ele, ninguém
 * administra usuários, permissões nem configurações). O backend ainda não recusa nenhum dos dois.
 */
export function porqueTravaPapelEAtivo(alvo: Pessoa | null | undefined, eu: Pessoa | null | undefined, usuarios: Pessoa[]): string | null {
  if (!alvo?.id) return null;
  if (eu?.id === alvo.id) return 'Você não pode mudar o seu próprio perfil nem se desativar: outro administrador faz isso.';
  if (alvo.role === 'admin' && alvo.isActive !== false && adminsAtivos(usuarios) <= 1) {
    return 'É o único administrador ativo: rebaixar ou desativar deixaria o HUB sem quem administre usuários e permissões.';
  }
  return null;
}

/** A segunda linha da trava, para o caminho do Salvar: o que mudou no form e a trava proíbe. */
export function recusaDaEdicaoDoUsuario(original: Pessoa | null | undefined, form: Pessoa, eu: Pessoa | null | undefined, usuarios: Pessoa[]): string | null {
  const motivo = porqueTravaPapelEAtivo(original, eu, usuarios);
  if (!motivo || !original) return null;
  const mudouPapel = (form.role || original.role) !== original.role;
  const mudouAtivo = (form.isActive !== false) !== (original.isActive !== false);
  return mudouPapel || mudouAtivo ? motivo : null;
}

/** D5: excluir usuário sai da tela; desativar corta o acesso na hora (o servidor confere `isActive` a cada requisição). */
export function confirmacaoDeDesativar(u: Pessoa) {
  return {
    title: 'Desativar o usuário?',
    message: `${u.name || `#${u.id}`}${u.email ? ` (${u.email})` : ''} perde o acesso na hora, inclusive nas sessões abertas.`,
    consequence: 'O histórico e o que ele registrou ficam. Para devolver o acesso, edite e marque "Usuário ativo".',
    confirmLabel: 'Desativar',
  };
}

/**
 * D6: a troca de senha pelo admin ENCERRA as sessões do usuário (backend #416, 12/10/2026: incrementa o tokenVersion E
 * revoga os refresh tokens). Antes dizia o contrário ("continuam valendo"), que era verdade até a #416.
 * ⛔ Este texto só é verdade com a #416 NO AR: a PR que o trocou entra depois do deploy do backend. O servidor não devolve
 * marca nenhuma na resposta, então a tela não tem como saber qual backend está rodando.
 */
export const FRASE_DA_SENHA_NOVA = 'Trocar a senha aqui encerra as sessões deste usuário; se for a sua, você precisará entrar de novo.';

/** D7: perfil e permissões são listas separadas. */
export const FRASE_DO_PERFIL = 'Trocar o perfil não muda as permissões deste usuário. Depois de salvar, ajuste em Permissões → "Aplicar Padrão do Perfil".';

// ---------------------------------------------------------------- Permissões (D8)

export function diferencaDePermissoes(antes: string[], depois: string[]): { entram: number; saem: number } {
  const a = new Set(antes);
  const d = new Set(depois);
  return { entram: depois.filter((p) => !a.has(p)).length, saem: antes.filter((p) => !d.has(p)).length };
}

export function fraseDaDiferenca(antes: string[], depois: string[]): string {
  const { entram, saem } = diferencaDePermissoes(antes, depois);
  if (entram === 0 && saem === 0) return 'Nenhuma mudança desde que abriu.';
  return `Ao salvar: +${entram} / −${saem} em relação ao que está gravado.`;
}

export function confirmacaoDoPadraoDoPerfil(nome: string, atuais: string[], padrao: string[]) {
  const { entram, saem } = diferencaDePermissoes(atuais, padrao);
  return {
    title: 'Aplicar o padrão do perfil?',
    message: `As ${atuais.length} permissões de ${nome} na tela são substituídas pelas ${padrao.length} do padrão (+${entram} / −${saem}).`,
    consequence: 'Nada é gravado ainda: confira e clique em "Salvar Permissões".',
    confirmLabel: 'Aplicar na tela',
  };
}

// ---------------------------------------------------------------- Histórico (D1)

export const DIAS_DA_LIMPEZA_PADRAO = 365;

export function dataDeCorte(dias: number, hoje: Date = new Date()): string {
  const d = new Date(hoje.getTime());
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
}

/** O que `GET /activity-logs/cleanup/preview` devolve (só os campos que a tela usa). */
export interface PreviaDaLimpeza {
  dias: number;
  /** `YYYY-MM-DD` BRT: tudo ANTES dela sai. */
  corteISO: string;
  total: number;
  /** O que SAI, já sem os poupados. É o número que o DELETE exige em `confirmar`. */
  aApagar: number;
  percentualQueSai: number;
  poupadas?: number;
  avisoDaPoupanca?: string | null;
}

/**
 * D1: o "Limpar histórico" (TRUNCATE da trilha de todos, num clique, sem confirm) saiu. No lugar, apagar o que é mais
 * velho que N dias pelo /cleanup. A1 (26/10/2026): a frase diz QUANTOS saem, pela prévia do servidor, que é o mesmo
 * número que vai no `confirmar` do DELETE. Antes ela dizia que o servidor não contava — ele conta desde 12/10.
 */
export function confirmacaoDaLimpeza(p: PreviaDaLimpeza) {
  const n = (v: number) => Number(v || 0).toLocaleString('pt-BR');
  const partes = [
    `Apagar ${n(p.aApagar)} de ${n(p.total)} registros do histórico (${Number(p.percentualQueSai) || 0}%)?`,
    `Saem os registros anteriores a ${fmtDate(p.corteISO)}, de todos os usuários, sem volta. O histórico é a única prova de quem fez o quê.`,
  ];
  if (p.avisoDaPoupanca) partes.push(p.avisoDaPoupanca);
  return partes.join('\n\n');
}

/** O 409 do DELETE: o número mudou entre a prévia e o clique (alguém agiu no meio). Nada foi apagado. */
export function recusaDaLimpeza(e: any): string | null {
  const d = e?.response?.status === 409 ? e?.response?.data : null;
  if (!d) return null;
  if (d.code === 'CONFIRMACAO_DIVERGENTE') return `O número mudou desde a contagem (agora ${Number(d.aApagar) || 0}). Nada foi apagado; clique de novo para conferir.`;
  return d.message ? `Nada foi apagado: ${d.message}` : 'Nada foi apagado: o servidor pediu confirmação do número.';
}

// ---------------------------------------------------------------- Produto (D12)

export function assinaturasQueUsam(productId: number, contracts: any[]): { total: number; ativas: number } {
  // `items` às vezes chega como texto JSON (o relatório de top produtos já trata assim).
  const itens = (c: any): any[] => {
    if (Array.isArray(c?.items)) return c.items;
    if (typeof c?.items === 'string') { try { const v = JSON.parse(c.items); return Array.isArray(v) ? v : []; } catch { return []; } }
    return [];
  };
  const usa = (c: any) => itens(c).some((i: any) => Number(i?.productId) === Number(productId));
  const lista = (contracts || []).filter(usa);
  return { total: lista.length, ativas: lista.filter((c: any) => c.status === 'Ativo').length };
}

/** D12: com uso, excluir trava. O backend não confere `contracts.items` (jsonb, sem FK), e o item fica apontando para nada. */
export function porqueNaoExcluirProduto(productId: number, contracts: any[]): string | null {
  const { total, ativas } = assinaturasQueUsam(productId, contracts);
  if (total === 0) return null;
  return `${total} assinatura${total > 1 ? 's usam' : ' usa'} este produto (${ativas} ativa${ativas === 1 ? '' : 's'}). Excluir deixaria o item apontando para um produto que não existe, e a nota pode sair do tipo errado. Inative o produto em vez de excluir.`;
}

// ---------------------------------------------------------------- DDR em lote (D9)

export type Recusa = { numero: string; motivo: string };

/** D9: o aviso do lote diz criados E recusados; com 0 criados não é "sucesso". */
export function resumoDoLote(criados: number, recusas: Recusa[]): { mensagem: string; tom: 'success' | 'warning' | 'error' } {
  const c = `${criados} DDR${criados === 1 ? '' : 's'} criado${criados === 1 ? '' : 's'}`;
  if (recusas.length === 0) return { mensagem: `${c}.`, tom: 'success' };
  const lista = recusas.slice(0, 5).map((r) => `${r.numero} (${r.motivo})`).join('; ');
  const resto = recusas.length > 5 ? `; e mais ${recusas.length - 5}` : '';
  return {
    mensagem: `${c}; ${recusas.length} recusado${recusas.length === 1 ? '' : 's'}: ${lista}${resto}. Os recusados ficaram no formulário para corrigir.`,
    tom: criados === 0 ? 'error' : 'warning',
  };
}
